<template>
  <!--
    A lock, drawn as the piece of hardware you actually touch — in the pump's
    glass idiom: one inline SVG on a 48-grid, translucent shells with one dark
    outline, and no decorative colour. The one colour it does carry is the state
    dot, because that is what the hardware itself shows.

    Two kinds (owner, 2026-10-26): `cylinder` is just the rotary knob with its
    indicator — red while the door is locked, green once it is open, the knob a
    quarter turn round; `strike` is a close-up of the door's edge, its lever and
    the strike cavity behind it, the keeper jaw either filling the cavity or
    swung clear of it.
  -->
  <svg
    class="lock-anim"
    :class="[`lock-anim--${kind}`, { 'lock-anim--unlocked': !locked }]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <!-- ── Rotary cylinder: the deadbolt body you actually turn — a capsule with
         the big thumb-turn low down and its indicator high up, red while the
         door is locked, green once it is released ─────────────────────────── -->
    <g v-if="kind === 'cylinder'">
      <rect class="lock-anim__casing" x="14.4" y="3.6" width="19.2" height="40.8" rx="9.4" />
      <path class="lock-anim__side" d="M33.6,13.2 V34.8 A9.4,9.4 0 0 1 31,42 V6 A9.4,9.4 0 0 1 33.6,13.2 Z" />
      <circle class="lock-anim__glass" cx="24" cy="14" r="3.4" />
      <circle class="lock-anim__dot" cx="24" cy="14" r="1.7" />
      <path class="lock-anim__seam" d="M15.6,20.6 H32.4" />
      <circle class="lock-anim__face" cx="24" cy="30.2" r="9.6" />
      <circle class="lock-anim__glass" cx="24" cy="30.2" r="6.6" />
      <g class="lock-anim__key" style="transform-origin: 24px 30.2px">
        <ellipse class="lock-anim__mech" cx="24" cy="30.2" rx="3.4" ry="4.4" />
        <path class="lock-anim__mech-line" d="M24,30.2 V24.6" />
      </g>
    </g>

    <!-- ── Electric strike: the door's edge, its lever, and the cavity ───── -->
    <g v-else>
      <rect class="lock-anim__casing" x="2.6" y="4.6" width="15.8" height="38.8" rx="2.4" />
      <path class="lock-anim__side" d="M18.4,6.6 V41.4 L21.2,40.2 V7.8 Z" />
      <circle class="lock-anim__face" cx="10.4" cy="20.4" r="4.6" />
      <path class="lock-anim__mech-line" d="M10.4,20.4 H19.4" />
      <circle class="lock-anim__glass" cx="10.4" cy="32.4" r="3.2" />
      <path class="lock-anim__seam" d="M10.4,29.2 V35.6" />
      <rect class="lock-anim__casing" x="27" y="4.6" width="18.4" height="38.8" rx="2.4" />
      <path class="lock-anim__side" d="M45.4,6.6 V41.4 L48,40.2 V7.8 Z" />
      <rect class="lock-anim__shadow" x="29.4" y="24.6" width="13.6" height="12.4" rx="1.6" />
      <g class="lock-anim__keeper" style="transform-origin: 29.4px 24.6px">
        <path class="lock-anim__mech" d="M29.4,24.6 H43 V31 H29.4 Z" />
      </g>
      <path class="lock-anim__seam" d="M31.4,10 H40.4" />
      <path class="lock-anim__seam" d="M31.4,15 H40.4" />
      <g class="lock-anim__buzz">
        <path d="M40.6,16.6 q2.4,2 0,4.4" />
        <path d="M43.4,14.6 q3.4,3.4 0,8.4" />
      </g>
      <circle class="lock-anim__dot" cx="24" cy="38.4" r="2.2" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import type { LockKind } from '../deviceWidgetTypes';

withDefaults(
  defineProps<{
    kind?: LockKind;
    /** Locked is the knob home and the cavity held; unlocked is the turn and the jaw clear. */
    locked?: boolean;
  }>(),
  { kind: 'cylinder', locked: true },
);
</script>

<style scoped>
.lock-anim {
  display: block;
  width: 100%;
  height: 100%;
  /* The pump's palette, in ink: a translucent shell, a denser part inside it,
     and one outline dark enough to hold both together. */
  --lock-shell: color-mix(in srgb, currentColor 30%, transparent);
  --lock-part: color-mix(in srgb, currentColor 46%, transparent);
  --lock-glass: color-mix(in srgb, currentColor 9%, transparent);
  --lock-side: color-mix(in srgb, currentColor 40%, transparent);
  --lock-move: color-mix(in srgb, currentColor 64%, transparent);
  --lock-edge: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  /* The hardware's own indicator: red while locked, green once released. */
  --lock-locked: #ef4444;
  --lock-open: #22c55e;
}

/* ── The casing: the plate, the door's edge, the jamb — semi-transparent so
      what is inside it shows through ──────────────────────────────────────── */
.lock-anim__casing {
  fill: var(--lock-shell);
  stroke: var(--lock-edge);
  stroke-width: 1.2;
}

/* A part behind the shell — denser, so the layering reads. */
.lock-anim__face {
  fill: var(--lock-part);
  stroke: var(--lock-edge);
  stroke-width: 1.1;
}

/* Look through it: the knob, the cylinder in section. */
.lock-anim__glass {
  fill: var(--lock-glass);
  stroke: var(--lock-edge);
  stroke-width: 1.1;
}

/* The cavity left behind when the keeper lets go. */
.lock-anim__shadow {
  fill: color-mix(in srgb, currentColor 14%, transparent);
  stroke: var(--lock-edge);
  stroke-opacity: 0.5;
  stroke-width: 0.9;
}

/* The depth band that turns a plate into a box. */
.lock-anim__side {
  fill: var(--lock-side);
}

.lock-anim__seam {
  fill: none;
  stroke: var(--lock-edge);
  stroke-opacity: 0.68;
  stroke-width: 0.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* ── The mechanism: what turns or swings ────────────────────────────────── */
.lock-anim__mech {
  fill: var(--lock-move);
  stroke: var(--lock-edge);
  stroke-width: 0.9;
  transition: transform 0.35s ease;
}

.lock-anim__mech-line {
  fill: none;
  stroke: var(--lock-move);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.35s ease;
}

.lock-anim__key,
.lock-anim__keeper {
  transform-box: view-box;
  transition: transform 0.35s ease;
}

/* ── The state dot: the one colour, because the hardware shows one ───────── */
.lock-anim__dot {
  fill: var(--lock-locked);
  stroke: color-mix(in srgb, currentColor 30%, transparent);
  stroke-width: 0.6;
  transition: fill 0.3s ease;
}

.lock-anim--unlocked .lock-anim__dot {
  fill: var(--lock-open);
}

/* ── The strike's own buzz while it holds the door ──────────────────────── */
.lock-anim__buzz path {
  fill: none;
  stroke: var(--lock-move);
  stroke-width: 1.4;
  stroke-linecap: round;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* ── Unlocked: the knob turns a quarter, the jaw swings clear ───────────── */
.lock-anim--unlocked .lock-anim__mech,
.lock-anim--unlocked .lock-anim__mech-line {
  filter: drop-shadow(0 0 1.4px color-mix(in srgb, currentColor 45%, transparent));
}

.lock-anim--cylinder.lock-anim--unlocked .lock-anim__key {
  transform: rotate(90deg);
}

.lock-anim--strike.lock-anim--unlocked .lock-anim__keeper {
  transform: rotate(-52deg);
}

.lock-anim--strike.lock-anim--unlocked .lock-anim__buzz path {
  opacity: 1;
  animation: lock-anim-buzz 1.1s ease-in-out infinite;
}

@keyframes lock-anim-buzz {
  0%,
  100% {
    opacity: 0.35;
    transform: translateX(0);
  }
  50% {
    opacity: 1;
    transform: translateX(1.2px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .lock-anim__mech,
  .lock-anim__mech-line,
  .lock-anim__key,
  .lock-anim__keeper,
  .lock-anim__dot {
    transition: none;
  }

  .lock-anim--strike.lock-anim--unlocked .lock-anim__buzz path {
    animation: none !important;
  }
}
</style>
